import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Circle, Polygon, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Trash2, MapPin, Locate } from 'lucide-react';

// Athens fallback center — most rides live around this bounding box
const DEFAULT_CENTER = [37.9838, 23.7275];
const DEFAULT_RADIUS_M = 1500;

function MapClickAdder({ onAdd }) {
  useMapEvents({
    click(e) {
      onAdd({ lat: e.latlng.lat, lng: e.latlng.lng, radius_m: DEFAULT_RADIUS_M });
    },
  });
  return null;
}

/**
 * Interactive circle editor for a pricing Area.
 * - Click on the map to add a new circle at that spot with the default radius.
 * - Each circle listed below the map with editable radius and a delete button.
 * - Renders read-only polygons (imported from KML/MyMaps) — polygons are not
 *   editable per-vertex here; delete them via the "polygons" list below the map.
 * - Bounded to Athens by default; user can pan/zoom anywhere.
 *
 * Props:
 *   circles: [{lat, lng, radius_m}, ...]
 *   polygons: [[{lat, lng}, ...], ...]
 *   onChange: ({circles, polygons}) => void
 *   onCirclesChange: (nextCircles) => void  (backwards-compatible for callers using only circles)
 */
export default function AreaMapEditor({ circles = [], polygons = [], onChange, onCirclesChange }) {
  const _emit = (nextCircles, nextPolygons) => {
    if (onChange) onChange({ circles: nextCircles, polygons: nextPolygons });
    if (onCirclesChange) onCirclesChange(nextCircles);
  };

  const [center, setCenter] = useState(() => {
    if (circles.length > 0) return [circles[0].lat, circles[0].lng];
    if (polygons.length > 0 && polygons[0]?.length > 0) return [polygons[0][0].lat, polygons[0][0].lng];
    return DEFAULT_CENTER;
  });

  useEffect(() => {
    // Auto-center on the first shape only if we're still at the default.
    if (center[0] !== DEFAULT_CENTER[0] || center[1] !== DEFAULT_CENTER[1]) return;
    if (circles.length > 0) setCenter([circles[0].lat, circles[0].lng]);
    else if (polygons.length > 0 && polygons[0]?.length > 0) setCenter([polygons[0][0].lat, polygons[0][0].lng]);
  }, [circles, polygons, center]);

  const addCircle = (c) => _emit([...(circles || []), c], polygons);
  const updateCircle = (idx, patch) => {
    const next = (circles || []).map((c, i) => (i === idx ? { ...c, ...patch } : c));
    _emit(next, polygons);
  };
  const removeCircle = (idx) => _emit((circles || []).filter((_, i) => i !== idx), polygons);
  const removePolygon = (idx) => _emit(circles, (polygons || []).filter((_, i) => i !== idx));

  const goToMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCenter([pos.coords.latitude, pos.coords.longitude]),
      () => { /* silent — keep default */ },
      { enableHighAccuracy: true, timeout: 5000 },
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5" />
          Click on the map to add a circle. Drag existing circle rows to adjust radius.
        </span>
        <button
          type="button"
          onClick={goToMyLocation}
          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
          data-testid="area-map-my-location"
        >
          <Locate className="h-3 w-3" /> My location
        </button>
      </div>

      <div className="rounded-md overflow-hidden border border-slate-200 h-[280px]">
        <MapContainer
          center={center}
          zoom={12}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
          key={`${center[0].toFixed(3)}-${center[1].toFixed(3)}`}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickAdder onAdd={addCircle} />
          {(circles || []).map((c, i) => (
            <Circle
              key={`c-${i}-${c.lat}-${c.lng}`}
              center={[c.lat, c.lng]}
              radius={c.radius_m || DEFAULT_RADIUS_M}
              pathOptions={{ color: '#059669', fillColor: '#10b981', fillOpacity: 0.18 }}
            />
          ))}
          {(polygons || []).map((poly, i) => (
            <Polygon
              key={`p-${i}`}
              positions={poly.map(v => [v.lat, v.lng])}
              pathOptions={{ color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 0.15, weight: 1.5 }}
            />
          ))}
        </MapContainer>
      </div>

      {(circles || []).length === 0 && (polygons || []).length === 0 && (
        <p className="text-xs text-slate-400 text-center py-1">
          No shapes yet. Click on the map above to add a circle, or use &quot;Import from KML&quot; to load polygons.
        </p>
      )}

      {(circles || []).map((c, i) => (
        <div
          key={i}
          className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5"
          data-testid={`circle-row-${i}`}
        >
          <span className="text-slate-500 w-6">#{i + 1}</span>
          <span className="font-mono text-slate-700 text-[11px]">
            {c.lat.toFixed(5)}, {c.lng.toFixed(5)}
          </span>
          <span className="text-slate-400">·</span>
          <label className="text-slate-500">radius</label>
          <Input
            type="number"
            min="50"
            step="50"
            value={c.radius_m}
            onChange={(e) => updateCircle(i, { radius_m: Math.max(0, Number(e.target.value) || 0) })}
            className="h-7 w-24 text-xs"
            data-testid={`circle-radius-${i}`}
          />
          <span className="text-slate-500">m</span>
          <div className="flex-1" />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-rose-500 hover:text-rose-600"
            onClick={() => removeCircle(i)}
            data-testid={`circle-delete-${i}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}

      {(polygons || []).map((poly, i) => (
        <div
          key={`poly-${i}`}
          className="flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1.5"
          data-testid={`polygon-row-${i}`}
        >
          <span className="text-slate-500 w-6">P{i + 1}</span>
          <span className="text-blue-700 font-medium">Polygon</span>
          <span className="text-slate-400">·</span>
          <span className="text-slate-600">{poly.length} vertices</span>
          <div className="flex-1" />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-rose-500 hover:text-rose-600"
            onClick={() => removePolygon(i)}
            data-testid={`polygon-delete-${i}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
    </div>
  );
}
